import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';
import AutocompleteInput from '@/components/AutocompleteInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Procedures = () => {
  const [procedures, setProcedures] = useState([]);
  const [patientProcedures, setPatientProcedures] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showProcedureForm, setShowProcedureForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState(null);
  const [procedureFormData, setProcedureFormData] = useState({
    name: '',
    duration: '',
    price: '',
    category: 'Panchakarma',
  });
  const [assignFormData, setAssignFormData] = useState({
    patientId: '',
    procedureId: '',
    sessionCount: '',
    therapistName: '',
    doctorName: '',
    patientName: '',
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/procedures/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchProcedures();
    } catch (error) {
      console.error('Error deleting procedure:', error);
      alert(error.response?.data?.detail || 'Failed to delete procedure');
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const fetchProcedures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/procedures`);
      setProcedures(response.data);
    } catch (error) {
      console.error('Error fetching procedures:', error);
    }
  }, []);

  const fetchPatientProcedures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patient-procedures`);
      setPatientProcedures(response.data);
    } catch (error) {
      console.error('Error fetching patient procedures:', error);
    }
  }, []);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  }, []);

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      // Doctors endpoint is open, but log gracefully
      console.error('Error fetching doctors:', error);
    }
  }, []);

  useEffect(() => {
    fetchProcedures();
    fetchPatientProcedures();
    fetchPatients();
    fetchDoctors();
  }, [fetchProcedures, fetchPatientProcedures, fetchPatients, fetchDoctors]);

  // Build autocomplete option lists from existing data
  const therapistOptions = useMemo(() => {
    return patientProcedures
      .map((pp) => ({ value: (pp.therapistName || '').trim() }))
      .filter((o) => o.value);
  }, [patientProcedures]);

  const doctorOptions = useMemo(() => {
    const fromDocs = doctors.map((d) => ({ value: d.name, hint: d.specialization || '' }));
    const fromPP = patientProcedures
      .map((pp) => ({ value: (pp.doctorName || '').trim() }))
      .filter((o) => o.value);
    return [...fromDocs, ...fromPP];
  }, [doctors, patientProcedures]);

  const patientNameOptions = useMemo(() => {
    const fromPatients = patients
      .filter((p) => !p.archived && (p.name || '').trim())
      .map((p) => ({ value: p.name, hint: p.phone || '' }));
    const fromPP = patientProcedures
      .map((pp) => ({ value: (pp.patientName || '').trim() }))
      .filter((o) => o.value);
    return [...fromPatients, ...fromPP];
  }, [patients, patientProcedures]);

  const handleProcedureSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProcedure) {
        await axios.put(`${API}/procedures/${editingProcedure.id}`, procedureFormData);
      } else {
        await axios.post(`${API}/procedures`, procedureFormData);
      }
      fetchProcedures();
      resetProcedureForm();
    } catch (error) {
      console.error('Error saving procedure:', error);
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build payload; trim text fields; coerce empty patientId to null so backend treats it as unlinked
      const payload = {
        ...assignFormData,
        patientId: assignFormData.patientId || null,
        sessionCount: parseInt(assignFormData.sessionCount, 10),
        therapistName: (assignFormData.therapistName || '').trim(),
        doctorName: (assignFormData.doctorName || '').trim(),
        patientName: (assignFormData.patientName || '').trim(),
      };
      await axios.post(`${API}/patient-procedures`, payload);
      fetchPatientProcedures();
      resetAssignForm();
    } catch (error) {
      console.error('Error assigning procedure:', error);
    }
  };

  const handleUpdateSessions = async (procedureId, completedSessions) => {
    try {
      await axios.put(`${API}/patient-procedures/${procedureId}/sessions`, null, {
        params: { completed: completedSessions },
      });
      fetchPatientProcedures();
    } catch (error) {
      console.error('Error updating sessions:', error);
    }
  };

  const handleEdit = (procedure) => {
    setEditingProcedure(procedure);
    setProcedureFormData({
      name: procedure.name,
      duration: procedure.duration,
      price: procedure.price,
      category: procedure.category,
    });
    setShowProcedureForm(true);
  };

  const resetProcedureForm = () => {
    setProcedureFormData({ name: '', duration: '', price: '', category: 'Panchakarma' });
    setEditingProcedure(null);
    setShowProcedureForm(false);
  };

  const resetAssignForm = () => {
    setAssignFormData({ patientId: '', procedureId: '', sessionCount: '', therapistName: '', doctorName: '', patientName: '' });
    setShowAssignForm(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return { bg: '#D1FAE5', text: '#065F46' };
      case 'IN_PROGRESS':
        return { bg: '#DBEAFE', text: '#1E40AF' };
      default:
        return { bg: '#FEE2E2', text: '#991B1B' };
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="procedures-title">
            Procedures
          </h1>
          <p className="mt-2" style={{ color: '#6B7280' }}>Manage Panchakarma procedures</p>
        </div>
        <div className="flex gap-3">
          <button
            className="primary-btn flex items-center gap-2"
            onClick={() => setShowProcedureForm(true)}
            data-testid="add-procedure-btn"
          >
            <Plus size={20} />
            Add Procedure
          </button>
          <button
            className="secondary-btn flex items-center gap-2"
            onClick={() => setShowAssignForm(true)}
            data-testid="assign-procedure-btn"
          >
            <Plus size={20} />
            Assign to Patient
          </button>
        </div>
      </div>

      {showProcedureForm && (
        <div className="card p-6 mb-8" data-testid="procedure-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            {editingProcedure ? 'Edit Procedure' : 'Add New Procedure'}
          </h2>
          <form onSubmit={handleProcedureSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label>Procedure Name</label>
                <input
                  type="text"
                  value={procedureFormData.name}
                  onChange={(e) => setProcedureFormData({ ...procedureFormData, name: e.target.value })}
                  required
                  data-testid="procedure-name-input"
                />
              </div>
              <div>
                <label>Duration (days)</label>
                <input
                  type="number"
                  value={procedureFormData.duration}
                  onChange={(e) => setProcedureFormData({ ...procedureFormData, duration: e.target.value })}
                  required
                  data-testid="procedure-duration-input"
                />
              </div>
              <div>
                <label>Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={procedureFormData.price}
                  onChange={(e) => setProcedureFormData({ ...procedureFormData, price: e.target.value })}
                  required
                  data-testid="procedure-price-input"
                />
              </div>
              <div>
                <label>Category</label>
                <input
                  type="text"
                  value={procedureFormData.category}
                  onChange={(e) => setProcedureFormData({ ...procedureFormData, category: e.target.value })}
                  required
                  data-testid="procedure-category-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="primary-btn" data-testid="procedure-submit-btn">
                {editingProcedure ? 'Update' : 'Save'}
              </button>
              <button type="button" className="secondary-btn" onClick={resetProcedureForm} data-testid="procedure-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {showAssignForm && (
        <div className="card p-6 mb-8" data-testid="assign-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            Assign Procedure to Patient
          </h2>
          <form onSubmit={handleAssignSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label>Patient (link, optional)</label>
                <select
                  value={assignFormData.patientId}
                  onChange={(e) => setAssignFormData({ ...assignFormData, patientId: e.target.value })}
                  data-testid="assign-patient-select"
                >
                  <option value="">— No patient linked —</option>
                  {patients.filter((p) => !p.archived).map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name || patient.phone}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Procedure <span style={{ color: '#EF4444' }}>*</span></label>
                <select
                  value={assignFormData.procedureId}
                  onChange={(e) => setAssignFormData({ ...assignFormData, procedureId: e.target.value })}
                  required
                  data-testid="assign-procedure-select"
                >
                  <option value="">Select Procedure</option>
                  {procedures.map((procedure) => (
                    <option key={procedure.id} value={procedure.id}>
                      {procedure.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Session Count <span style={{ color: '#EF4444' }}>*</span></label>
                <input
                  type="number"
                  min="1"
                  value={assignFormData.sessionCount}
                  onChange={(e) => setAssignFormData({ ...assignFormData, sessionCount: e.target.value })}
                  required
                  data-testid="assign-session-count-input"
                />
              </div>
              <div>
                <label>Patient name (if not linked)</label>
                <AutocompleteInput
                  value={assignFormData.patientName}
                  onChange={(v) => setAssignFormData({ ...assignFormData, patientName: v })}
                  options={patientNameOptions}
                  placeholder="Walk-in / unlinked patient"
                  testIdPrefix="assign-patient-name"
                />
              </div>
              <div>
                <label>Doctor's name</label>
                <AutocompleteInput
                  value={assignFormData.doctorName}
                  onChange={(v) => setAssignFormData({ ...assignFormData, doctorName: v })}
                  options={doctorOptions}
                  placeholder="Referring doctor"
                  testIdPrefix="assign-doctor-name"
                />
              </div>
              <div>
                <label>Therapist name</label>
                <AutocompleteInput
                  value={assignFormData.therapistName}
                  onChange={(v) => setAssignFormData({ ...assignFormData, therapistName: v })}
                  options={therapistOptions}
                  placeholder="Performing therapist"
                  testIdPrefix="assign-therapist-name"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="primary-btn" data-testid="assign-submit-btn">
                Assign
              </button>
              <button type="button" className="secondary-btn" onClick={resetAssignForm} data-testid="assign-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
          Available Procedures
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {procedures.map((procedure) => (
            <div key={procedure.id} className="card p-6" data-testid={`procedure-card-${procedure.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
                    {procedure.name}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: '#6B7280' }}>{procedure.category}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(procedure)}
                    className="p-2 rounded hover:bg-gray-100 transition-colors"
                    data-testid={`edit-procedure-${procedure.id}`}
                  >
                    <Edit size={18} style={{ color: '#990099' }} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(procedure)}
                    className="p-2 rounded hover:bg-gray-100 transition-colors"
                    data-testid={`delete-procedure-${procedure.id}`}
                  >
                    <Trash2 size={18} style={{ color: '#EF4444' }} />
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span style={{ color: '#78716C' }}>Duration:</span>
                  <span className="font-medium" style={{ color: '#2D3A2A' }}>{procedure.duration} days</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#78716C' }}>Price:</span>
                  <span className="font-medium" style={{ color: '#990099' }}>₹{procedure.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
          Patient Procedures
        </h2>
        <div className="card p-6">
          <div className="overflow-x-auto">
            <table data-testid="patient-procedures-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Procedure</th>
                  <th>Doctor</th>
                  <th>Therapist</th>
                  <th>Sessions</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {patientProcedures.map((pp) => {
                  const linkedPatient = patients.find((p) => p.id === pp.patientId);
                  const displayName = linkedPatient?.name || linkedPatient?.phone || pp.patientName || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Unlinked</span>;
                  const statusColor = getStatusColor(pp.status);
                  return (
                    <tr key={pp.id} data-testid={`patient-procedure-row-${pp.id}`}>
                      <td className="font-medium" style={{ color: '#2D3A2A' }}>
                        {linkedPatient ? (
                          <Link to={`/patients/${linkedPatient.id}/history`} style={{ color: '#990099' }} data-testid={`pp-patient-link-${pp.id}`}>
                            {displayName}
                          </Link>
                        ) : (
                          displayName
                        )}
                      </td>
                      <td>{pp.procedureName}</td>
                      <td style={{ color: pp.doctorName ? '#2D3A2A' : '#9CA3AF' }}>{pp.doctorName || '—'}</td>
                      <td style={{ color: pp.therapistName ? '#2D3A2A' : '#9CA3AF' }}>{pp.therapistName || '—'}</td>
                      <td>{pp.completedSessions}/{pp.sessionCount}</td>
                      <td>
                        <div className="w-full bg-gray-200 rounded-full h-2" style={{ minWidth: 80 }}>
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{
                              width: `${(pp.completedSessions / pp.sessionCount) * 100}%`,
                              backgroundColor: '#990099',
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                        >
                          {pp.status}
                        </span>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          max={pp.sessionCount}
                          value={pp.completedSessions}
                          onChange={(e) => handleUpdateSessions(pp.id, parseInt(e.target.value))}
                          className="w-20"
                          data-testid={`update-sessions-${pp.id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {patientProcedures.length === 0 && (
              <div className="text-center py-8" style={{ color: '#6B7280' }} data-testid="no-patient-procedures">
                No procedures assigned yet
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Procedure"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? If this procedure is currently assigned to any patient, the deletion will be blocked.`}
        confirmLabel="Delete Procedure"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        testIdPrefix="delete-procedure"
      />
    </div>
  );
};

export default Procedures;
